import { PolicyAccident } from '@models/policy-accident';
import { PolicyCar } from '@models/policy-car';
import { PolicyDeath } from '@models/policy-death';
import { PolicyHealth } from '@models/policy-health';
import { PolicyHome } from '@models/policy-home';
import { PolicyLife } from '@models/policy-life';
import { PolicyOther } from '@models/policy-other';
import { PolicyPension } from '@models/policy-pension';
import { PolicySaving } from '@models/policy-saving';
import { SigningKeyPositions } from '@models/profile';
import { padZeros } from '@utils/utils';

export type EnabledPolicyToChangeBankAccount =
  | PolicyLife
  | PolicyPension
  | PolicyOther
  | PolicyHome
  | PolicyHealth
  | PolicySaving
  | PolicyAccident
  | PolicyDeath
  | PolicyCar;

export type PolicyHavingBankIban =
  | PolicyCar
  | PolicyHome
  | PolicyOther
  | PolicyLife
  | PolicySaving
  | PolicyHealth
  | PolicyDeath
  | PolicyAccident;

export interface SigningKeyStatus {
  dateLastModified: string;
  status: string;
}
export interface BankAccountBodyRequest {
  accountType: BankAccountType;
  iban: string;
  policyTypeName: string;
  signingKey?: SigningKeyPositions;
  bank?: string;
}

export interface BankDataChangeProcess extends BankAccountBodyRequest {
  mode: 'EXISTING' | 'NEW';
  oldIban: string;
}

export const SIGNING_KEY_ATTEMPTS = 3;

export type BankAccountType = 'CONTRIBUTION' | 'INCOME' | 'CREDIT' | 'RECEIPT';
export type BankAccountChangeStatus = 'ENABLED' | 'LOCKED' | 'NOT_SHOWN';

export const parseBankEditStatus = (status: boolean): BankAccountChangeStatus =>
  status ? 'ENABLED' : 'LOCKED';

export const BankAccountCardTitles = {
  RECEIPTS: 'CLIENT_AREA.CA_PRODUCT_DETAIL.BANK_DATA.RECEIPTS_FUNDS',
  CONTRIBUTION: 'CLIENT_AREA.CA_PRODUCT_DETAIL.BANK_DATA.CONTRIBUTIONS_FUNDS',
  CREDIT: 'CLIENT_AREA.CA_PRODUCT_DETAIL.BANK_DATA.CREDIT_FUNDS',
  INCOME: 'CLIENT_AREA.CA_PRODUCT_DETAIL.BANK_DATA.INCOME_FUNDS'
} as const;

export interface BankAccount {
  iban?: string;
  bank?: string;
  accountType?: BankAccountType;
}
export interface BankAccountView extends BankAccount {
  editAvailable?: BankAccountChangeStatus;
  title: string;
  showSigningKeyInfo?: boolean;
}

export type BankChangeRestriction =
  | PendingChangeRestriction
  | BankDataSignKeyRestriction
  | null;

export interface BankDataSignKeyRestriction {
  type: 'SIGNING_KEY';
  restriction: SigningKeyStatus;
}

export interface PendingChangeRestriction {
  type: 'HAS_PENDING_CHANGE';
  restriction: PendingBankAccountChange;
}

export interface PendingBankAccountChange {
  type?: string;
  effectDate: string;
  isFuture?: boolean;
  mechanisationDate?: string;
}

export const BANK_ACCOUNT_CHANGE_ERROR_CODES = {
  INVALID_DATA: 'M#03000',
  GENERIC_ERROR: 'M#03001'
} as const;

// DD/MM/YYYY
export const getParsedDate = (effectDate: string) => {
  const date = new Date(effectDate);
  const day = padZeros(date.getDate().toString(), 2);
  const month = padZeros((date.getMonth() + 1).toString(), 2);
  const year = date.getFullYear().toString();

  return `${day}/${month}/${year}`;
};

export const setWhiteSpacesInIban = (iban: string) =>
  iban
    .trim()
    .replace(/[^\dA-Z]/g, '')
    .replace(/(.{4})/g, '$1 ')
    .trim();
